import React, { useState, useEffect, useRef } from 'react';
import { Map, MapPin, DollarSign, Users, TrendingUp, RefreshCw, Download, ChevronLeft } from 'lucide-react';
import { formatDateForInput } from '../utils/dateUtils';
import MaskedDatePicker from './MaskedDatePicker';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import THAILAND_PROVINCE_COORDINATES from '../config/thailandProvinceCoordinates';

// Component สำหรับ Legend บนแผนที่
const MapLegend = () => {
  const map = useMap();
  const legendRef = useRef(null);

  useEffect(() => {
    if (!legendRef.current) return;
    // ป้องกันไม่ให้ scroll/drag บน legend ส่งผลต่อแผนที่
    const el = legendRef.current;
    const L = require('leaflet');
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
  }, []);

  const levels = [
    { color: '#9ca3af', label: 'ไม่มียอดขาย' },
    { color: '#86efac', label: '< 100K' },
    { color: '#fde047', label: '100K - 500K' },
    { color: '#fb923c', label: '500K - 1M' },
    { color: '#f87171', label: '1M - 5M' },
    { color: '#dc2626', label: '> 5M' },
  ];

  return (
    <div
      ref={legendRef}
      style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        zIndex: 1000,
        background: 'white',
        padding: '12px 16px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontSize: '13px',
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#374151' }}>ระดับยอดขาย</div>
      {levels.map((l) => (
        <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div
            style={{
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: l.color,
              border: '1px solid #d1d5db',
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#4b5563' }}>{l.label}</span>
        </div>
      ))}
    </div>
  );
};

const SalesMapByProvinceComponent = ({ onBack }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [provinceSalesData, setProvinceSalesData] = useState(null);
  const [error, setError] = useState('');

  // จังหวัดทั้งหมดในประเทศไทย (77 จังหวัด)
  const allThaiProvinces = [
    'กรุงเทพมหานคร', 'กระบี่', 'กาญจนบุรี', 'กาฬสินธุ์', 'กำแพงเพชร',
    'ขอนแก่น', 'จันทบุรี', 'ฉะเชิงเทรา', 'ชลบุรี', 'ชัยนาท', 'ชัยภูมิ', 'ชุมพร',
    'เชียงราย', 'เชียงใหม่', 'ตรัง', 'ตราด', 'ตาก', 'นครนายก', 'นครปฐม',
    'นครพนม', 'นครราชสีมา', 'นครศรีธรรมราช', 'นครสวรรค์', 'นนทบุรี', 'นราธิวาส',
    'น่าน', 'บึงกาฬ', 'บุรีรัมย์', 'ปทุมธานี', 'ประจวบคีรีขันธ์', 'ปราจีนบุรี',
    'ปัตตานี', 'พะเยา', 'พังงา', 'พัทลุง', 'พิจิตร', 'พิษณุโลก', 'เพชรบุรี',
    'เพชรบูรณ์', 'แพร่', 'ภูเก็ต', 'มหาสารคาม', 'มุกดาหาร', 'แม่ฮ่องสอน',
    'ยโสธร', 'ยะลา', 'ร้อยเอ็ด', 'ระนอง', 'ระยอง', 'ราชบุรี', 'ลพบุรี', 'ลำปาง',
    'ลำพูน', 'เลย', 'ศรีสะเกษ', 'สกลนคร', 'สงขลา', 'สตูล', 'สมุทรปราการ',
    'สมุทรสงคราม', 'สมุทรสาคร', 'สระแก้ว', 'สระบุรี', 'สิงห์บุรี', 'สุโขทัย',
    'สุพรรณบุรี', 'สุราษฎร์ธานี', 'สุรินทร์', 'หนองคาย', 'หนองบัวลำภู',
    'อ่างทอง', 'อำนาจเจริญ', 'อุดรธานี', 'อุตรดิตถ์', 'อุทัยธานี', 'อุบลราชธานี'
  ];

  const initializeDateRange = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setStartDate(formatDate(sevenDaysAgo));
    setEndDate(formatDate(today));
  };

  const getMinAllowedDate = () => {
    return '2025-11-01';
  };

  const getMaxAllowedDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    initializeDateRange();
  }, []);

  const fetchProvinceSalesData = async () => {
    setLoading(true);
    setError('');
    setProvinceSalesData(null);

    try {
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const today = new Date();
      const minDate = new Date('2025-11-01');

      if (startDateObj < minDate) {
        setError('วันที่เริ่มต้นต้องไม่ก่อนวันที่ 1 พฤศจิกายน 2025');
        setLoading(false);
        return;
      }

      if (endDateObj > today) {
        setError('วันที่สิ้นสุดต้องไม่เกินวันปัจจุบัน');
        setLoading(false);
        return;
      }

      if (startDateObj > endDateObj) {
        setError('วันที่เริ่มต้นต้องมาก่อนวันที่สิ้นสุด');
        setLoading(false);
        return;
      }

      console.log('Step 1: Fetching customer summary data...');
      const customerRequestBody = {
        date_range: {
          start_date: startDate,
          end_date: endDate,
          group_by: "month"
        },
        filters: {
          company_code: "045"
        }
      };

      const customerResponse = await fetch('https://apisaleeasy.psisat.com/api/sales-analytics/customer-summary', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(customerRequestBody)
      });

      if (!customerResponse.ok) {
        throw new Error(`Customer API error! status: ${customerResponse.status}`);
      }

      const customerData = await customerResponse.json();

      if (!customerData.status || !customerData.data || !customerData.data.customers) {
        setError('ไม่พบข้อมูลลูกค้าในช่วงเวลาที่เลือก');
        setLoading(false);
        return;
      }

      console.log(`Found ${customerData.data.customers.length} customers`);

      console.log('Step 2: Fetching customer details for province mapping...');
      const customers = customerData.data.customers;
      const customerProvinces = {};
      const provinceCustomerCounts = {};
      const provinceSalesAmounts = {};
      const provinceInvoiceCounts = {};

      const customerDetailsPromises = customers.map(async (customer) => {
        try {
          const response = await fetch('https://apisaleeasy.psisat.com/api/technician/by-code', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code: String(customer.customer_code).trim() })
          });

          if (response.ok) {
            const result = await response.json();
            let province = '-';

            if (result && result.status && result.data) {
              province = result.data.technician_province || result.data.province || result.data.customer_province || '-';
            } else if (result && result.data && !result.status) {
              province = result.data.technician_province || result.data.province || result.data.customer_province || '-';
            } else if (result && Object.keys(result).length > 0 && !result.status && !result.data) {
              province = result.technician_province || result.province || result.customer_province || '-';
            }

            return {
              customer_code: customer.customer_code,
              province: province,
              total_amount: customer.total_amount || 0,
              total_bills: customer.total_bills || 0
            };
          }
          return null;
        } catch (error) {
          console.warn(`Failed to fetch details for customer ${customer.customer_code}:`, error);
          return null;
        }
      });

      const customerDetailsResults = await Promise.allSettled(customerDetailsPromises);

      customerDetailsResults.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          const customerDetail = result.value;
          let province = customerDetail.province || 'ไม่ระบุจังหวัด';

          if (province !== '-' && province !== 'ไม่ระบุจังหวัด') {
            province = province.replace(/^จังหวัด\s*/, '').replace(/กรุงเทพฯ?$/, 'กรุงเทพมหานคร').trim();

            const matchedProvince = allThaiProvinces.find(p =>
              p === province ||
              p.includes(province) ||
              province.includes(p)
            );

            if (matchedProvince) {
              province = matchedProvince;
            }
          }

          customerProvinces[customerDetail.customer_code] = province;

          if (province && province !== '-' && province !== 'ไม่ระบุจังหวัด') {
            if (!provinceCustomerCounts[province]) {
              provinceCustomerCounts[province] = 0;
              provinceSalesAmounts[province] = 0;
              provinceInvoiceCounts[province] = 0;
            }

            provinceCustomerCounts[province]++;
            provinceSalesAmounts[province] += customerDetail.total_amount;
            provinceInvoiceCounts[province] += customerDetail.total_bills;
          }
        }
      });

      const allProvincesData = allThaiProvinces.map(provinceName => {
        const customerCount = provinceCustomerCounts[provinceName] || 0;
        const totalAmount = provinceSalesAmounts[provinceName] || 0;
        const invoiceCount = provinceInvoiceCounts[provinceName] || 0;

        return {
          province_name: provinceName,
          total_amount: totalAmount,
          customer_count: customerCount,
          invoice_count: invoiceCount
        };
      });

      const totalCustomers = Object.keys(customerProvinces).length;
      const totalAmount = Object.values(provinceSalesAmounts).reduce((sum, amount) => sum + amount, 0);
      const totalInvoices = Object.values(provinceInvoiceCounts).reduce((sum, count) => sum + count, 0);
      const provincesWithSales = Object.keys(provinceCustomerCounts).length;

      setProvinceSalesData({
        province_sales: allProvincesData,
        summary: {
          total_customers: totalCustomers,
          total_amount: totalAmount,
          total_invoices: totalInvoices,
          provinces_with_sales: provincesWithSales
        },
        period: {
          start_date: startDate,
          end_date: endDate
        }
      });

    } catch (error) {
      console.error('Error fetching province sales data:', error);
      setError('เกิดข้อผิดพลาดในการเชื่อมต่อ: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // กำหนดสีตามยอดขาย
  const getProvinceColor = (amount) => {
    if (amount === 0) return '#9ca3af';    // เทา - ไม่มียอดขาย
    if (amount < 100000) return '#86efac';  // เขียวอ่อน
    if (amount < 500000) return '#fde047';  // เหลือง
    if (amount < 1000000) return '#fb923c'; // ส้ม
    if (amount < 5000000) return '#f87171'; // แดงอ่อน
    return '#dc2626';                       // แดงเข้ม - ยอดขายสูงมาก
  };

  // กำหนดขนาดวงกลมตามยอดขาย
  const getCircleRadius = (amount) => {
    if (amount === 0) return 6;
    if (amount < 100000) return 10;
    if (amount < 500000) return 14;
    if (amount < 1000000) return 18;
    if (amount < 5000000) return 22;
    return 28;
  };

  // จับคู่ข้อมูลยอดขายกับพิกัดจังหวัด
  const getMapMarkers = () => {
    if (!provinceSalesData?.province_sales) return [];

    return THAILAND_PROVINCE_COORDINATES.map(coord => {
      const salesData = provinceSalesData.province_sales.find(p => {
        // จับคู่ชื่อจังหวัด (รองรับชื่อที่อาจต่างกันเล็กน้อย)
        return p.province_name === coord.name ||
               p.province_name.includes(coord.name) ||
               coord.name.includes(p.province_name);
      });

      return {
        ...coord,
        total_amount: salesData?.total_amount || 0,
        customer_count: salesData?.customer_count || 0,
        invoice_count: salesData?.invoice_count || 0,
      };
    });
  };

  const formatNumber = (num) => {
    return num.toLocaleString('th-TH');
  };

  const exportToExcel = () => {
    if (!provinceSalesData) return;

    const dataToExport = provinceSalesData.province_sales.map((province, index) => ({
      'ลำดับ': index + 1,
      'จังหวัด': province.province_name,
      'ยอดขาย (บาท)': province.total_amount.toLocaleString(),
      'จำนวนลูกค้า': province.customer_count,
      'จำนวนใบแจ้งหนี้': province.invoice_count
    }));

    console.log('Export data:', dataToExport);
    alert('ฟังก์ชันการส่งออกจะพัฒนาต่อไป');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Map className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">แผนที่ยอดขายตามจังหวัด</h2>
            <p className="text-gray-600">ข้อมูลยอดขายแยกตามจังหวัดทั่วประเทศไทย</p>
          </div>
        </div>
      </div>

      {/* Date Range Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          เลือกช่วงเวลา
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่เริ่มต้น
            </label>
            <MaskedDatePicker
              value={startDate}
              onChange={setStartDate}
              placeholderText="DD/MM/YYYY"
              minDate={new Date(getMinAllowedDate() + 'T00:00:00')}
              maxDate={new Date(getMaxAllowedDate() + 'T00:00:00')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              วันที่สิ้นสุด
            </label>
            <MaskedDatePicker
              value={endDate}
              onChange={setEndDate}
              placeholderText="DD/MM/YYYY"
              minDate={new Date(getMinAllowedDate() + 'T00:00:00')}
              maxDate={new Date(getMaxAllowedDate() + 'T00:00:00')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <button
            onClick={fetchProvinceSalesData}
            disabled={loading || !startDate || !endDate}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 justify-center"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <TrendingUp className="w-4 h-4" />
            )}
            {loading ? 'กำลังโหลด...' : 'ค้นหาข้อมูล'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">กำลังโหลดข้อมูล...</span>
        </div>
      )}

      {/* Results */}
      {provinceSalesData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">ยอดขายรวมทั้งหมด</p>
                  <p className="text-2xl font-bold">
                    ฿{(provinceSalesData.summary?.total_amount || 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">จังหวัดที่มียอดขาย</p>
                  <p className="text-2xl font-bold">
                    {provinceSalesData.summary?.provinces_with_sales || 0}
                  </p>
                </div>
                <MapPin className="w-8 h-8 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">ลูกค้าทั้งหมด</p>
                  <p className="text-2xl font-bold">
                    {provinceSalesData.summary?.total_customers || 0}
                  </p>
                </div>
                <Users className="w-8 h-8 text-purple-200" />
              </div>
            </div>
          </div>

          {/* Leaflet Map */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Map className="w-5 h-5 text-blue-600" />
              แผนที่ยอดขายประเทศไทย
            </h3>
            <div style={{ height: '600px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <MapContainer
                center={[13.0, 101.0]}
                zoom={6}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {getMapMarkers().map((marker) => (
                  <CircleMarker
                    key={marker.name}
                    center={[marker.lat, marker.lng]}
                    radius={getCircleRadius(marker.total_amount)}
                    pathOptions={{
                      color: '#ffffff',
                      weight: 2,
                      fillColor: getProvinceColor(marker.total_amount),
                      fillOpacity: 0.85,
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: '180px', fontFamily: 'Kanit, Sarabun, sans-serif' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '8px', color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: '6px' }}>
                          {marker.name}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#6b7280' }}>ยอดขาย:</span>
                          <span style={{ fontWeight: '600', color: marker.total_amount > 0 ? '#059669' : '#9ca3af' }}>
                            ฿{formatNumber(marker.total_amount)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#6b7280' }}>ลูกค้า:</span>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>{marker.customer_count} คน</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>ใบแจ้งหนี้:</span>
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>{marker.invoice_count} ใบ</span>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}

                <MapLegend />
              </MapContainer>
            </div>
          </div>

          {/* Table View */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">รายละเอียดยอดขายตามจังหวัด</h3>
              <button
                onClick={exportToExcel}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                ส่งออก Excel
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      จังหวัด
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ยอดขาย (บาท)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      จำนวนลูกค้า
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      จำนวนใบแจ้งหนี้
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      สถานะ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {provinceSalesData.province_sales
                    ?.sort((a, b) => b.total_amount - a.total_amount)
                    .map((province) => (
                    <tr
                      key={province.province_name}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-4 h-4 rounded-full"
                            style={{backgroundColor: getProvinceColor(province.total_amount)}}
                          ></div>
                          <span className="text-sm font-medium text-gray-900">
                            {province.province_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <span className={`font-semibold ${province.total_amount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          ฿{province.total_amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {province.customer_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {province.invoice_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          province.total_amount > 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {province.total_amount > 0 ? 'มียอดขาย' : 'ไม่มียอดขาย'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SalesMapByProvinceComponent;
